import { ArrowDropDown } from '@mui/icons-material';
import { Box, Button, Divider, Grid, Popover } from '@mui/material';
import strings from 'common/Translation/Translate';
import { useMemo, useState } from 'react';
import { bookingDetailToolbarButtonSx } from 'screens/Authenticated/BookingDetail/bookingDetailToolbarSx';
import { identifiers } from 'utils/constants/identifiers';

const CONTRACT = identifiers.CONTRACT;
const PROPOSAL = identifiers.PROPOSAL;
const PI = identifiers.PI;
const ENQUIRY = identifiers.ENQUIRY;

type PreviewPopoverProps = {
  isContractTemplatePresent: boolean;
  isProposalTemplatePresent: boolean;
  isPiTemplatePresent: boolean;
  isEnquiryTemplatePresent: boolean;
  downloadContract: any;
  handlePreviewContract: any;
  disabled?: any;
  /** Compact outlined trigger aligned to booking detail toolbar buttons (not full width). */
  toolbarStyle?: boolean;
  /** Render each available template as its own button instead of a dropdown. */
  inline?: boolean;
};
export const PreviewPopover = ({
  isContractTemplatePresent,
  isProposalTemplatePresent,
  isPiTemplatePresent,
  isEnquiryTemplatePresent,
  handlePreviewContract: handlePreviewTemplate,
  disabled = false,
  toolbarStyle = false,
  inline = false,
}: PreviewPopoverProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const openPopover = Boolean(anchorEl);

  const enabledTemplates = useMemo(() => {
    const templates: string[] = [];
    if (isEnquiryTemplatePresent) templates.push(ENQUIRY);
    if (isContractTemplatePresent && !isEnquiryTemplatePresent) templates.push(CONTRACT);
    if (isProposalTemplatePresent) templates.push(PROPOSAL);
    if (isPiTemplatePresent) templates.push(PI);
    return templates;
  }, [isContractTemplatePresent, isProposalTemplatePresent, isPiTemplatePresent, isEnquiryTemplatePresent]);

  const getLabel = (type: string) => {
    switch (type) {
      case CONTRACT:
        return strings.bookings_button_preview_contract;
      case PROPOSAL:
        return strings.bookings_button_preview_proposal;
      case ENQUIRY:
        return strings.bookings_button_preview_enquiry;
      case PI:
        return strings.bookings_button_preview_pi;
      default:
        return '';
    }
  };

  const handleClick = (type: string) => {
    handlePreviewTemplate(type);
  };

  const triggerSx = toolbarStyle
    ? {
        ...bookingDetailToolbarButtonSx,
        minHeight: 32,
        height: 32,
        px: 1.25,
        fontSize: '0.75rem',
        borderColor: 'divider',
        whiteSpace: 'nowrap' as const,
        width: 'auto',
      }
    : { whiteSpace: 'normal' as const };

  if (enabledTemplates.length === 0) {
    return (
      <Button
        fullWidth={!toolbarStyle}
        size={toolbarStyle ? 'small' : 'medium'}
        disabled
        variant="outlined"
        color={toolbarStyle ? 'inherit' : 'primary'}
        sx={triggerSx}>
        No templates are available
      </Button>
    );
  }

  if (inline || enabledTemplates.length === 1) {
    const buttons = enabledTemplates.map(type => (
      <Button
        key={type}
        fullWidth={!toolbarStyle && !inline}
        size={toolbarStyle || inline ? 'small' : 'medium'}
        onClick={() => handleClick(type)}
        disabled={disabled}
        variant="outlined"
        color={toolbarStyle || inline ? 'inherit' : 'primary'}
        sx={inline ? { ...bookingDetailToolbarButtonSx, whiteSpace: 'nowrap' } : triggerSx}>
        {getLabel(type)}
      </Button>
    ));

    if (!inline) {
      return buttons[0];
    }

    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1 }}>{buttons}</Box>
    );
  }

  return (
    <>
      <Button
        fullWidth={!toolbarStyle}
        size={toolbarStyle ? 'small' : 'medium'}
        onClick={(event: any) => setAnchorEl(event.currentTarget)}
        disabled={disabled}
        variant="outlined"
        color={toolbarStyle ? 'inherit' : 'primary'}
        endIcon={<ArrowDropDown sx={{ ml: -0.5 }} />}
        sx={triggerSx}>
        {strings.bookings_button_preview}
      </Button>

      <Popover
        open={openPopover}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}>
        <Grid container flexDirection="column" py={1} px={0.5} sx={{ minWidth: 180 }}>
          {enabledTemplates.map((type, idx) => (
            <Grid item key={type}>
              <Button
                fullWidth
                onClick={() => {
                  handleClick(type);
                  setAnchorEl(null);
                }}
                disabled={disabled}
                variant="text"
                sx={{
                  justifyContent: 'flex-start',
                  minHeight: 36,
                  px: 1.5,
                  textTransform: 'none',
                  fontSize: '0.8125rem',
                }}>
                {getLabel(type)}
              </Button>
              {idx < enabledTemplates.length - 1 && <Divider />}
            </Grid>
          ))}
        </Grid>
      </Popover>
    </>
  );
};
